<template>
    <exportable-table v-model="list" rowKey="resource_id" :headers="headers" :loading="loading" :filename="filename">
        <template v-slot:rowTitle="{ item }">
            <div class="flex justify-between items-center gap-2 max-w-72">
                <div class="flex w-full gap-2 items-start">
                    <div class="min-w-16">
                        <vx-badge
                            class="cursor-pointer"
                            @click.prevent="copyText(item.resource_id)"
                            size="xs"
                            color="slate"
                        >
                            {{ item.resource_id }}
                        </vx-badge>
                    </div>
                    <div class="break-words line-clamp-4">
                        {{ item.resource_name || '-' }}
                    </div>
                </div>

                <div class="flex gap-2">
                    <vx-popover hover arrow placement="top">
                        <font-awesome-icon
                            widthAuto
                            class="h-4 w-4"
                            :class="getSenderTypeColorClass(item.sender_type)"
                            :icon="getSenderTypeIcon(item.sender_type, 'regular')"
                        />

                        <template v-slot:content>{{ senderTypeLabel(item.sender_type) }}</template>
                    </vx-popover>

                    <vx-popover hover arrow placement="top">
                        <font-awesome-icon
                            widthAuto
                            class="h-4 w-4 text-slate-500"
                            :icon="resourceTypeIcon(item.resource_type, 'regular')"
                        />

                        <template v-slot:content>{{ resourceTypeLabel(item.resource_type) }}</template>
                    </vx-popover>
                </div>
            </div>
        </template>
        <template v-slot:cell="{ value, field }">
            <template v-if="field === 'total_messages_sent'">
                <vx-popover
                    v-if="formatCount(value) !== ''"
                    arrow
                    hover
                    placement="top"
                    class="cursor-pointer"
                    @click="copyText(value)"
                >
                    {{ formatCount(value) }}
                    <template v-slot:content>
                        <div class="text-center" @click="copyText(value)">
                            {{ Intl.NumberFormat('en-US').format(value) }}
                            <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                        </div>
                    </template>
                </vx-popover>
                <span v-else>-</span>
            </template>
            <template v-else-if="metricsWithGoals.includes(field)">
                <vx-popover
                    v-if="value !== null"
                    arrow
                    hover
                    placement="top"
                    @click="copyText(formatFractionToPercent(value, true))"
                    class="cursor-pointer"
                >
                    {{ formatPercentage(value, true) }}
                    <template v-slot:content>
                        <div class="text-center" @click="copyText(formatFractionToPercent(value, true))">
                            {{ formatFractionToPercent(value, true) }}
                            <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                        </div>
                    </template>
                </vx-popover>
                <span v-else>-</span>
            </template>
            <template v-else-if="field === 'messaging_cost' && showEstimatedCost">
                <vx-popover
                    v-if="value !== null && value !== 0"
                    arrow
                    hover
                    placement="top"
                    @click="copyText(currencyCents(value))"
                    class="cursor-pointer"
                >
                    {{ vxFormatCentsAsDollars(value, true) }}
                    <template v-slot:content>
                        <div class="text-center" @click="copyText(currencyCents(value))">
                            {{ currencyCents(value) }}
                            <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                        </div>
                    </template>
                </vx-popover>
                <span v-else>-</span>
            </template>
            <template v-else>
                {{ value }}
            </template>
        </template>
        <template v-slot:buttons="{ item }">
            <exportable-table-row-buttons>
                <vx-button @click="selectedResource = item" color="muted" outline size="sm" class="grow sm:grow-0">
                    View <font-awesome-icon widthAuto :icon="faEye" />
                </vx-button>

                <resource-detail-modal v-model="selectedResource" :resource="item" :category="props.category" />
            </exportable-table-row-buttons>
        </template>
    </exportable-table>
</template>

<script setup>
import ExportableTable from '~/components/analytics/partial/exportable-table/ExportableTable.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { outboundMessagesAnalyticsClient } from '~/services';
import { useListAllPaginated } from '~/composables/useListAllPaginated';
import {
    metricAbbreviation,
    metricLabel,
    metricsInputValidator,
    resourceTypeIcon,
    metricsWithGoals,
    getSenderTypeIcon,
    getSenderTypeColorClass,
    resourceTypeLabel,
    addCategoryToFetchParams,
} from '~/utils/analytics';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { formatPercentage, formatCount } from '~/components/filters';
import { VxBadge, VxButton, VxPopover, vxFormatCentsAsDollars } from '@voxie/frontend-components';
import { useCopyText } from '~/composables/useCopyText';
import { currencyCents, formatFractionToPercent } from '~/utils/helpers';
import { senderTypeLabel } from '~/constants/senderTypes';
import { faEye } from '@fortawesome/pro-solid-svg-icons';
import ExportableTableRowButtons from '../../partial/exportable-table/ExportableTableRowButtons.vue';
import ResourceDetailModal from '../../partial/resource-detail/ResourceDetailModal.vue';
import { analyticsCategoriesValues } from '~/constants/categories';
import { useAnalyticsTargets } from '~/components/analytics/useAnalyticsTargets';
import { useCostVisibility } from '~/composables/useCostVisibility';

const teamId = Spark.state.currentTeam.id;
const props = defineProps({
    orgUnitId: {
        type: Number,
        required: false,
    },
    groupId: {
        type: String,
        required: false,
    },
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    filename: {
        type: String,
        required: true,
    },
    category: {
        type: String,
        required: false,
        validator: (category) => analyticsCategoriesValues.includes(category),
    },
});

const fetchAllParams = ref();
const { list, fetchAll, loading } = useListAllPaginated(fetchAllParams);
const { fetchTargets } = useAnalyticsTargets();

const { copyText } = useCopyText();
const { showEstimatedCost } = useCostVisibility();

const selectedResource = ref();

const headers = computed(() => {
    const baseHeaders = [
        { field: 'resource_id', hidden: true },
        { field: 'resource_name', label: 'Name', title: true, sortable: true },
        { field: 'sender_type', hidden: true },
        { field: 'resource_type', hidden: true },
        {
            field: 'total_messages_sent',
            label: metricAbbreviation('total_messages_sent'),
            popoverContent: metricLabel('total_messages_sent'),
            sortable: true,
        },
        {
            field: 'unique_click_through_rate',
            label: metricAbbreviation('unique_click_through_rate'),
            popoverContent: metricLabel('unique_click_through_rate'),
            sortable: true,
        },
        {
            field: 'click_through_rate',
            label: metricAbbreviation('click_through_rate'),
            popoverContent: metricLabel('click_through_rate'),
            sortable: true,
        },
        {
            field: 'opt_out_rate',
            label: metricAbbreviation('opt_out_rate'),
            popoverContent: metricLabel('opt_out_rate'),
            sortable: true,
        },
        {
            field: 'message_reply_rate',
            label: metricAbbreviation('message_reply_rate'),
            popoverContent: metricLabel('message_reply_rate'),
            sortable: true,
        },
    ];

    if (showEstimatedCost.value) {
        baseHeaders.push({
            field: 'messaging_cost',
            label: 'Cost',
            sortable: true,
            exportFormatter: (value) => (value !== null ? (value / 100).toFixed(2) : null),
        });
    }

    return baseHeaders;
});

watch(
    () => props.fetchParams,
    () => {
        fetchAllParams.value = JSON.parse(JSON.stringify(props.fetchParams));
        const fetchParams = props.category
            ? addCategoryToFetchParams(fetchAllParams.value, props.category)
            : fetchAllParams.value;
        fetchAll(() =>
            outboundMessagesAnalyticsClient.getExportResourceMetrics(
                {
                    teamId,
                    orgUnitId: props.orgUnitId,
                    groupId: props.groupId,
                },
                fetchParams
            )
        );
    },
    {
        deep: true,
        immediate: true,
    }
);

onMounted(() => {
    fetchTargets(teamId);
});
</script>
